import { createBackendSession } from '@/api/session'
import type { User } from '@/composables/useAuth'
import { useAuth } from '@/composables/useAuth'

interface IdentityState {
  input: {
    redirectUrl: string
  }
  currentUser: User
  userCreationPending: boolean
}

export const useIdentityStore = defineStore('identity', {
  state: (): IdentityState => ({
    input: {
      redirectUrl: '',
    },
    currentUser: {
      isAuthenticated: false,
    },
    userCreationPending: false,
  }),
  actions: {
    setRedirectUrl(redirectUrl: string) {
      this.input.redirectUrl = redirectUrl
    },
    clearRedirectUrl() {
      this.input.redirectUrl = ''
    },
    async getCurrentUser() {
      this.currentUser = await useAuth().getUser()

      if (this.userCreationPending && this.currentUser.isAuthenticated) {
        try {
          const accessToken = await this.getRequiredAccessToken()
          await createBackendSession(accessToken)
          this.userCreationPending = false
        } catch (error) {
          console.error('Error creating backend session:', error)
          throw error
        }
      }

      return this.currentUser
    },
    async getRequiredAccessToken() {
      let accessToken: string | undefined
      let retries = 0
      const maxRetries = 5
      const baseDelay = 200

      while (!accessToken && retries < maxRetries) {
        accessToken = await useAuth().getAccessToken(retries > 0)

        if (!accessToken) {
          const delay = baseDelay * Math.pow(2, retries)
          await new Promise((resolve) => setTimeout(resolve, delay))
          retries++
        }
      }

      if (!accessToken) {
        throw new Error('Access token is not available. Please ensure you are authenticated.')
      }

      return accessToken
    },
  },
})
